/**
 * Displacement Summary Service — Matches Xamarin MiddlewareService.GetTrailersDisplacementSummary()
 *
 * Calls OIC directly (same pattern as ManhattanService / MoveTaskService).
 * Endpoint: NBL_YMS_GET_DISPLA_TRAILE/1.0/getDisplacedTrailers?size={0}&org_id={1}&user_id={2}
 * Method: POST
 * Request body: { TraceData: { User, Plant, SessionId } }
 */

import { YMS_API_CONFIG } from '@/constants';
import { store } from '@/store';

import { getOICBaseUrl, getAuthHeader, oicFetch } from './ManhattanService';
import { mapDisplacedSummaryResponse } from './types/displacementSummaryTypes';

import type {
  DisplacedSummaryResponseDTO,
  DisplacedSummaryResponse,
} from './types/displacementSummaryTypes';

// ─────────────────────────────────────────────────────────────────
// Helpers — same pattern as ManhattanService
// ─────────────────────────────────────────────────────────────────

function getOrgId(): string {
  return String(store.getState().user.plant?.org_id ?? '');
}

function getUserId(): string {
  const state = store.getState();
  const user = state.user.user;
  if (!user) return '';
  return `${user.country_code}${user.mobile_no}`;
}

// ─────────────────────────────────────────────────────────────────
// API Call — Matches Xamarin MiddlewareService.GetTrailersDisplacementSummary()
// Calls OIC directly: NBL_YMS_GET_DISPLA_TRAILE/1.0/getDisplacedTrailers
// ─────────────────────────────────────────────────────────────────

export async function getDisplacementSummary(): Promise<DisplacedSummaryResponse> {
  const baseUrl = getOICBaseUrl();

  if (!baseUrl) {
    throw new Error('MA_API_BASE_URL is not configured');
  }

  const orgId = getOrgId();
  const userId = getUserId();

  // Matches Xamarin: displacedSummaryEndpointAPI =
  // "NBL_YMS_GET_DISPLA_TRAILE/1.0/getDisplacedTrailers?size={0}&org_id={1}&user_id={2}"
  const params = new URLSearchParams({
    size: '',
    org_id: orgId,
    user_id: userId,
  });

  const endpointUrl = `${baseUrl}${YMS_API_CONFIG.MAGetDisplacedTrailersAPI}?${params.toString()}`;

  // Matches Xamarin: auth type switch (Bearer or Basic) via getAuthHeader()
  const authHeader = await getAuthHeader();

  const response = await oicFetch(endpointUrl, {
    method: 'GET',
    headers: {
      Authorization: authHeader,
    },
  });

  if (!response.ok) {
    throw new Error(
      `Displacement Summary API error: ${String(response.status)} ${response.statusText}`
    );
  }

  const data = (await response.json()) as DisplacedSummaryResponseDTO;
  return mapDisplacedSummaryResponse(data);
}

// ─────────────────────────────────────────────────────────────────
// Export facade
// ─────────────────────────────────────────────────────────────────

export const displacementSummaryApi = {
  getDisplacementSummary,
};
